import type { Product } from '@scayle/storefront-nuxt'
import { useDebounceFn } from '@vueuse/core'

type ListedProduct = Product & { index: number }

interface UseProductIntersectionCollectionOptions {
  debounceDelay?: number
  perPage?: number
  age?: number
  currentPage?: MaybeRefOrGetter<number>
  onIntersection?: (items: ListedProduct[]) => void
}

/**
 * Composable for collecting and managing product intersection events
 * Provides debounced collection of products that have intersected the viewport
 */
export default function useProductIntersectionCollection(
  options: UseProductIntersectionCollectionOptions = {},
) {
  const { debounceDelay = 100, perPage, currentPage, onIntersection } = options

  const intersectionQueue = ref<ListedProduct[]>([])
  const _currentPage = toRef(currentPage)

  const flushIntersectionQueue = useDebounceFn(() => {
    if (!intersectionQueue.value.length) {
      return
    }

    const items = [...intersectionQueue.value]

    if (onIntersection) {
      onIntersection(items)
    }

    intersectionQueue.value = []
    return items
  }, debounceDelay)

  /**
   * Collect a product intersection event
   * @param product - The product that intersected
   * @param index - The local index of the product in the current list
   */
  const collectIntersection = (product: Product, index: number) => {
    const globalIndex =
      perPage && _currentPage.value
        ? index + perPage * (_currentPage.value - 1)
        : index

    intersectionQueue.value.push({
      ...product,
      index: globalIndex,
    })

    flushIntersectionQueue()
  }

  return {
    collectIntersection,
  }
}
